import { Audio } from "../utils/Audio"
import { PhaserText } from "../utils/PhaserText"
import { Scale } from "../utils/Scale"
import { Buttons } from "./Buttons"
import { Rectangle } from "./Shape"


export interface DropdownInfo<T extends string> {
    text: T
    audio: string
}

class DropDownConfig<T extends string> {
    x?: number
    y?: number
    placeholder?: string
    fontPx?: number = 18
    blankNum?: 1 | 2 | 3 | 4 | 5
    group?: Phaser.Group
    onSelect?: (selected: T) => void
    startEnabled?: boolean
}

export class DropDownMenu<T extends string> {

    private readonly dropGroup: Phaser.Group
    private enabled: boolean

    constructor(
        private game: Phaser.Game, optionArray: Array<DropdownInfo<T>>,
        { x, y, fontPx = 18, placeholder, group, onSelect, startEnabled = true }: DropDownConfig<T> = {}
    ) {
        // adjustment for placement
        y -= 8
        this.dropGroup = game.add.group()
        const paddingX = Scale.x(0.01)
        const paddingY = Scale.y(0.01)
        const scaledHeightFromPx = Math.floor(fontPx * Scale.scaleAmount())
        let width = Scale.x(.15)
        let height = scaledHeightFromPx

        this.enabled = startEnabled

        const closeDropDownOverlay = new Rectangle(game, 0, 0, Scale.x(1), Scale.y(1), {
            fillAlpha: 0.2,
            fillColor: 0x000000,
            group: this.dropGroup,
        })

        const list = PhaserText.list(game, { fontPx, x: x + paddingX, y, group: this.dropGroup })
        const selectOptions = optionArray.map(option => {
            const text = list(option.text)
            height += text.height
            width = text.width > width ? text.width : width
            return { text: text, audio: option.audio, value: option.text }
        })

        // DropDown background
        new Rectangle(game, x, y, width + paddingX * 2, height, {
            fillColor: 0xFFFFFF,
            lineWidth: 1,
            group: this.dropGroup
        })

        this.dropGroup.visible = false

        const answerBox = new Rectangle(game, x, y - scaledHeightFromPx - paddingY,
            width + paddingX * 2, scaledHeightFromPx + paddingY, { lineWidth: 1, group }
        )

        const blank = PhaserText.create(game, placeholder, {
            x: x + paddingX,
            y: y - scaledHeightFromPx - paddingY,
            fontPx,
            group
        })

        const setBlank = (text: string) => {
            blank.setText(text)
            this.dropGroup.visible = false
        }

        Buttons.makeSpriteButton(closeDropDownOverlay.invisibleSprite, () => this.dropGroup.visible = false)

        selectOptions.forEach((option) => {
            const optionText = option.text
            optionText.bringToTop()
            const hitArea = new Rectangle(game, optionText.x, optionText.y, width, optionText.height, {
                lineWidth: 0,
                group: this.dropGroup
            })
            Buttons.makeSpriteButton(hitArea.invisibleSprite, () => {
                Audio.add(this.game, option.audio)
                setBlank(optionText.text)
                if (onSelect) {
                    onSelect(option.value)
                }
            })
        })

        Buttons.makeSpriteButton(answerBox.invisibleSprite, () => {
            if (this.enabled) {
                game.world.bringToTop(this.dropGroup)
                this.dropGroup.visible = !this.dropGroup.visible
            }
        })
    }

    enable = () => this.enabled = true

}
